/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    Optional,
    Self,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormBuilder,
    FormControl,
    FormGroup,
    NG_VALUE_ACCESSOR,
    NgControl,
} from '@angular/forms';
import { MatFormField, MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { DateInterval } from '../../../../types';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { DropDownItem } from '../../../../modules/reports/state/dataForm.model';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { isEqual } from 'lodash-es';
import moment from 'moment';
import { DashGisUtil } from '../../../../modules/shared/dash-gis-util';
import { LoggerService } from '../../../../modules/shared/services/logger.service';
import { View } from 'ol';
import { SelectEvent } from 'ol/interaction/Select';
import { MatSelect, MatSelectChange } from '@angular/material/select';

@Component({
    selector: 'dash-gis-date-range',
    templateUrl: './date-range.component.html',
    styleUrls: ['./date-range.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: DateRangeComponent,
        },
    ],
})
export class DateRangeComponent implements OnInit, OnChanges, ControlValueAccessor, MatFormFieldControl<DateInterval> {
    private static nextId = 0;

    interval: FormGroup;

    @ViewChild('dateSelect') dateSelect: MatSelect;

    @Input() options: DropDownItem[];
    @Input() label: string;
    dateOptions: DropDownItem[];

    // region MatFormFieldControl
    @Input()
    get value() {
        return {
            min: this.interval.value['min'],
            max: this.interval.value['max'],
        };
    }

    set value(value: DateInterval) {
        this.interval.patchValue({
            min: value?.min,
            max: value?.max,
        });
        this.stateChanges.next();
    }

    @Input()
    get required(): boolean {
        return this.requiredInternal;
    }

    set required(value: boolean) {
        this.requiredInternal = coerceBooleanProperty(value);
        this.stateChanges.next();
    }

    @Input()
    get disabled(): boolean {
        return this.disabledInternal;
    }

    set disabled(value: boolean) {
        this.disabledInternal = coerceBooleanProperty(value);
        if (this.disabledInternal) {
            this.interval.disable();
        } else {
            this.interval.enable();
        }
        this.stateChanges.next();
    }

    stateChanges = new Subject<void>();

    @HostBinding()
    id = `dash-gis-date-dropdown-${DateRangeComponent.nextId++}`;

    placeholder = '';
    focused = false;
    shouldLabelFloat = false;
    controlType = 'dash-gis-date-range';
    userAriaDescribedBy?: string;
    // endregion

    touched = false;

    private requiredInternal = false;
    private disabledInternal: boolean;
    private dispose$ = new Subject<void>();

    onChange = (_: any) => {};
    onTouched = () => {};

    constructor(
        @Optional() @Self() public ngControl: NgControl,
        @Optional() public parentFormField: MatFormField,
        private logger: LoggerService,
        private elementRef: ElementRef<HTMLElement>
    ) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
        this.interval = new FormGroup({
            min: new FormControl(null, { updateOn: 'blur' }),
            max: new FormControl(null, { updateOn: 'blur' }),
        });
    }

    // region Angular Lifecycle
    ngOnInit(): void {
        this.interval.valueChanges
            .pipe(
                takeUntil(this.dispose$),
            )
            .subscribe(() => this.onChange(this.value));
    }

    ngOnChanges(changes: SimpleChanges) {
        const change = changes.options;

        if (change) {
            const prev: DropDownItem[] = change.previousValue;
            const curr: DropDownItem[] = change.currentValue;

            if (!isEqual(prev, curr)) {
                this.dateOptions = curr.map((opt, index, options) => {
                    const dateOpt: DropDownItem = DashGisUtil.clone(opt);
                    if (opt.value?.min) {
                        dateOpt.value['min'] = moment(opt.value.min).toDate();
                    }
                    if (opt.value?.max) {
                        dateOpt.value['max'] = moment(opt.value.max).toDate();
                    }
                    return dateOpt;
                });
            }
        }
    }

    ngOnDestroy(): void {
        this.stateChanges.complete();
        this.dispose$.next();
    }

    // endregion

    // region ControlValueAccessor
    writeValue(val: DateInterval): void {
        this.value = val;
        this.updateSelect(val);
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    // endregion

    // region MatFormFieldControl
    setDescribedByIds(ids: string[]): void {
        const controlElement = this.elementRef.nativeElement.querySelector('.dash-gis-date-range-container');
        controlElement.setAttribute('aria-describedby', ids.join(' '));
    }

    onContainerClick(event: MouseEvent): void {}

    get errorState(): boolean {
        return this.interval.invalid && this.touched;
    }

    get empty(): boolean {
        const n = this.interval.value;
        return !n.min && !n.max;
    }

    // endregion

    onFocusIn(event: FocusEvent) {
        if (!this.focused) {
            this.focused = true;
            this.stateChanges.next();
        }
    }

    onFocusOut(event: FocusEvent) {
        if (!this.elementRef.nativeElement.contains(event.relatedTarget as Element)) {
            this.touched = true;
            this.focused = false;
            this.onTouched();
            this.stateChanges.next();
        }
    }

    applySelect(event: MatSelectChange): void {
        if (event.value?.min || event.value?.max) {
            this.value = event.value;
        }
    }

    updateSelect(value: DateInterval): void {
        if (!this.dateSelect) {
            return;
        }
        const options = this.dateSelect.options;
        for (let idx = 0; idx < options.length; idx++) {
            const opt = options.get(idx);
            if (this.compareOptions(opt.value, value)) {
                this.dateSelect.value = opt.value;
                return;
            }
        }
        if (value?.min || value?.max) {
            this.dateSelect.value = 'custom';
        }
    }

    compareOptions(a, b): boolean {
        if (a === undefined || b === undefined) {
            return a === b;
        }
        return isEqual(a?.min, b?.min) && isEqual(a?.max, b?.max);
    }

    get shouldDisplaySelect(): boolean {
        return this?.options?.length > 0;
    }
}
